import React, { useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import styled, { keyframes } from 'styled-components'
import { NextLinkFromReactRouter } from 'components/NextLink'
import { 
  SwapIcon, 
  SwapFillIcon, 
  EarnIcon, 
  EarnFillIcon, 
  TrophyIcon, 
  TrophyFillIcon, 
  NftIcon, 
  NftFillIcon, 
  MoreIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@pancakeswap/uikit'
import { useTranslation } from 'contexts/Localization'
import { mainnetTokens } from 'config/constants/tokens'
import { useFetchPairPrices, useSingleTokenSwapInfo } from 'state/swap/hooks'
import { PairDataTimeWindowEnum } from 'state/swap/types'
import { useMenuItems } from '../Menu/hooks/useMenuItems'
import { getActiveMenuItem } from '../Menu/utils'
import Header from '../Header'

const slideIn = keyframes`
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(0);
  }
`

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(139, 92, 246, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(139, 92, 246, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(139, 92, 246, 0);
  }
`

const glow = keyframes`
  0% {
    filter: drop-shadow(0 0 5px rgba(255, 215, 0, 0.3));
  }
  50% {
    filter: drop-shadow(0 0 20px rgba(255, 215, 0, 0.6));
  }
  100% {
    filter: drop-shadow(0 0 5px rgba(255, 215, 0, 0.3));
  }
`

const SidebarContainer = styled.div<{ isCollapsed: boolean }>`
  position: fixed;
  top: 80px;
  left: 0;
  height: calc(100vh - 80px);
  width: ${({ isCollapsed }) => (isCollapsed ? '70px' : '260px')};
  background: ${({ theme }) => theme.colors.backgroundAlt};
  border-right: 1px solid ${({ theme }) => theme.colors.cardBorder};
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 998;
  display: flex;
  flex-direction: column;
  animation: ${slideIn} 0.3s ease-out;
  overflow: hidden;

  @media (max-width: 768px) {
    top: 70px;
    height: calc(100vh - 70px);
    width: ${({ isCollapsed }) => (isCollapsed ? '0' : '280px')};
    transform: ${({ isCollapsed }) => (isCollapsed ? 'translateX(-100%)' : 'translateX(0)')};
    box-shadow: ${({ isCollapsed }) => 
      isCollapsed 
        ? 'none'
        : '4px 0 20px rgba(0, 0, 0, 0.15)'
    };
  }
`

const SidebarHeader = styled.div<{ isCollapsed: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ isCollapsed }) => (isCollapsed ? 'center' : 'space-between')};
  padding: ${({ isCollapsed }) => (isCollapsed ? '20px 0' : '20px 24px')};
  border-bottom: 1px solid ${({ theme }) => theme.colors.cardBorder};
  min-height: 80px;
  position: relative;
`

const LogoContainer = styled.div<{ isCollapsed: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  opacity: 1;
  transform: scale(1);
  transition: all 0.3s ease;
  justify-content: ${({ isCollapsed }) => (isCollapsed ? 'center' : 'flex-start')};
`

const LogoText = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  margin: 0;
`

const LogoImage = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  object-fit: cover;
`

const LogoIconFallback = styled.div`
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary} 0%, ${({ theme }) => theme.colors.primaryBright} 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  color: white;
`

const CollapseButton = styled.button`
  background: ${({ theme }) => theme.colors.backgroundAlt2};
  border: none;
  border-radius: 12px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  color: ${({ theme }) => theme.colors.text};

  &:hover {
    background: ${({ theme }) => theme.colors.tertiary};
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`

const SidebarNav = styled.nav`
  flex: 1;
  padding: 20px 0;
  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 2px;
  }
`

const NavItem = styled.div<{ isActive: boolean; isCollapsed: boolean }>`
  margin: ${({ isCollapsed }) => (isCollapsed ? '4px 8px' : '2px 12px')};
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${({ isActive, theme }) => {
      if (isActive) {
        return `linear-gradient(90deg, rgba(0, 212, 170, 0.15) 0%, rgba(27, 255, 204, 0.15) 100%)`
      }
      return 'transparent'
    }};
    border-radius: 12px;
    transition: all 0.3s ease;
  }

  &:hover::before {
    background: ${({ theme }) => theme.colors.backgroundAlt2};
  }
`

const NavLink = styled.a<{ isActive: boolean; isCollapsed: boolean }>`
  display: flex;
  align-items: center;
  padding: ${({ isCollapsed }) => (isCollapsed ? '16px 23px' : '16px 20px')};
  text-decoration: none;
  color: ${({ isActive, theme }) => {
    if (isActive) {
      return theme.colors.primary
    }
    return theme.colors.textSubtle
  }};
  font-weight: ${({ isActive }) => (isActive ? '600' : '500')};
  font-size: 16px;
  position: relative;
  z-index: 1;
  transition: all 0.3s ease;
  gap: 16px;
  justify-content: ${({ isCollapsed }) => (isCollapsed ? 'flex-start' : 'flex-start')};

  &:hover {
    color: ${({ theme }) => theme.colors.text};
    transform: ${({ isCollapsed }) => (isCollapsed ? 'none' : 'translateX(4px)')};
  }

  svg {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
  }
`

const NavText = styled.span<{ isCollapsed: boolean }>`
  opacity: ${({ isCollapsed }) => (isCollapsed ? '0' : '1')};
  transform: ${({ isCollapsed }) => (isCollapsed ? 'translateX(-10px)' : 'translateX(0)')};
  transition: all 0.3s ease;
  white-space: nowrap;
  animation: ${({ isCollapsed }) => (isCollapsed ? 'none' : fadeIn)} 0.3s ease;
`

const SubNavContainer = styled.div<{ isOpen: boolean; isCollapsed: boolean }>`
  max-height: ${({ isOpen, isCollapsed }) => (isOpen && !isCollapsed ? '300px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease;
  background: ${({ theme }) => theme.colors.backgroundAlt2};
  margin: 0 16px;
  border-radius: 12px;
  margin-top: 4px;
`

const SubNavItem = styled.a`
  display: block;
  padding: 12px 20px 12px 60px;
  color: ${({ theme }) => theme.colors.textSubtle};
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  border-radius: 8px;
  margin: 4px 8px;

  &:hover {
    background: ${({ theme }) => theme.colors.tertiary};
    color: ${({ theme }) => theme.colors.text};
    transform: translateX(4px);
  }
`

const SidebarFooter = styled.div<{ isCollapsed: boolean }>`
  padding: 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.cardBorder};
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const PriceDisplay = styled.div<{ isCollapsed: boolean }>`
  background: linear-gradient(135deg, rgba(0, 212, 170, 0.1) 0%, rgba(27, 255, 204, 0.1) 100%);
  border-radius: 12px;
  padding: 12px;
  text-align: center;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  opacity: ${({ isCollapsed }) => (isCollapsed ? '0' : '1')};
  transform: ${({ isCollapsed }) => (isCollapsed ? 'scale(0.8)' : 'scale(1)')};
  transition: all 0.3s ease;
`

const MobileBottomNav = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 70px;
  background: ${({ theme }) => theme.colors.backgroundAlt};
  border-top: 1px solid ${({ theme }) => theme.colors.cardBorder};
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  display: none;
  align-items: center;
  justify-content: space-around;
  padding: 8px 16px;
  z-index: 1000;

  @media (max-width: 768px) {
    display: flex;
  }
`

const MobileNavItem = styled.div<{ isActive: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  border-radius: 12px;
  transition: all 0.3s ease;
  cursor: pointer;
  min-width: 60px;
  background: ${({ isActive, theme }) => {
    if (isActive) {
      return 'rgba(0, 212, 170, 0.15)'
    }
    return 'transparent'
  }};

  &:hover {
    background: ${({ theme }) => theme.colors.backgroundAlt2};
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`

const MobileNavIcon = styled.div<{ isActive: boolean }>`
  color: ${({ isActive, theme }) => {
    if (isActive) {
      return theme.colors.primary
    }
    return theme.colors.textSubtle
  }};
  transition: all 0.3s ease;
  margin-bottom: 4px;

  svg {
    width: 22px;
    height: 22px;
  }
`

const MobileNavText = styled.span<{ isActive: boolean }>`
  font-size: 11px;
  font-weight: ${({ isActive }) => (isActive ? '600' : '500')};
  color: ${({ isActive, theme }) => {
    if (isActive) {
      return theme.colors.primary
    }
    return theme.colors.textSubtle
  }};
  transition: all 0.3s ease;
  text-align: center;
  line-height: 1.2;
`

const MainContent = styled.div<{ isCollapsed: boolean }>`
  margin-left: ${({ isCollapsed }) => (isCollapsed ? '70px' : '260px')};
  margin-top: 80px;
  transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-height: calc(100vh - 80px);
  background: ${({ theme }) => theme.colors.background};

  @media (max-width: 768px) {
    margin-left: 0;
    margin-top: 70px;
    min-height: calc(100vh - 70px);
    padding-bottom: 70px;
  }
`

const DesktopSidebar = styled.div`
  @media (max-width: 768px) {
    display: none;
  }
`

interface SidebarProps {
  children: React.ReactNode
}

const Sidebar: React.FC<SidebarProps> = ({ children }) => {
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [expandedItem, setExpandedItem] = useState<string | null>(null)
  const { t } = useTranslation()
  const { pathname } = useRouter()

  const menuItems = useMenuItems()
  const activeMenuItem = getActiveMenuItem({ menuConfig: menuItems, pathname })

  const brandName = process.env.NEXT_PUBLIC_BRAND_NAME || 'GrxSwap'
  const logoUrl = process.env.NEXT_PUBLIC_BRAND_LOGO_URL

  const emptySwapPrice = useMemo<Record<string, number>>(() => ({}), [])
  const wgrxAddress = mainnetTokens.wgrx.address.toLowerCase()

  const grxPriceMap = useSingleTokenSwapInfo('GRX', mainnetTokens.wgrx, mainnetTokens.usdt.address, mainnetTokens.usdt)
  const currentSwapPrice = grxPriceMap ?? emptySwapPrice

  const { pairPrices: grxPairPrices } = useFetchPairPrices({
    token0Address: mainnetTokens.wgrx.address,
    token1Address: mainnetTokens.usdt.address,
    timeWindow: PairDataTimeWindowEnum.DAY,
    currentSwapPrice,
  })

  const grxPriceFromChart = useMemo(() => {
    if (!grxPairPrices || grxPairPrices.length === 0) {
      return null
    }
    const latestPoint = grxPairPrices[grxPairPrices.length - 1]
    if (!latestPoint || !latestPoint.value || Number.isNaN(latestPoint.value) || latestPoint.value <= 0) {
      return null
    }
    return latestPoint.value
  }, [grxPairPrices])

  const grxPriceFromTrade = useMemo(() => {
    if (!grxPriceMap) {
      return null
    }
    const price = grxPriceMap[wgrxAddress] ?? grxPriceMap[mainnetTokens.wgrx.address]
    if (!price || Number.isNaN(price) || price <= 0) {
      return null
    }
    return price
  }, [grxPriceMap, wgrxAddress])

  const grxPriceUsd = grxPriceFromTrade ?? grxPriceFromChart ?? null

  const formattedGrxPrice = useMemo(() => {
    if (grxPriceUsd === null) {
      return null
    }
    return grxPriceUsd.toLocaleString(undefined, {
      minimumFractionDigits: grxPriceUsd < 1 ? 4 : 2,
      maximumFractionDigits: grxPriceUsd < 1 ? 6 : 4,
    })
  }, [grxPriceUsd])

  const handleItemClick = (href: string, hasSubItems: boolean) => {
    if (hasSubItems && !isCollapsed) {
      setExpandedItem(expandedItem === href ? null : href)
    }
  }

  return (
    <>
      {/* Header */}
      <Header />
      
      {/* Desktop Sidebar */}
      <DesktopSidebar>
        <SidebarContainer isCollapsed={isCollapsed}>
          <SidebarHeader isCollapsed={isCollapsed}>
            <LogoContainer isCollapsed={isCollapsed}>
              {logoUrl ? (
                <LogoImage src={logoUrl} alt={brandName} />
              ) : (
                <LogoIconFallback>G</LogoIconFallback>
              )}
              {!isCollapsed && <LogoText>{brandName}</LogoText>}
            </LogoContainer>
            {!isCollapsed && (
              <CollapseButton 
                onClick={() => setIsCollapsed(!isCollapsed)}
              >
                <ChevronLeftIcon />
              </CollapseButton>
            )}
            {isCollapsed && (
              <CollapseButton 
                onClick={() => setIsCollapsed(!isCollapsed)}
                style={{ 
                  position: 'absolute', 
                  top: '50%', 
                  right: '-15px', 
                  transform: 'translateY(-50%)',
                  background: '#01051d',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  zIndex: 1001
                }}
              >
                <ChevronRightIcon />
              </CollapseButton>
            )}
          </SidebarHeader>

          <SidebarNav>
            {menuItems.filter(item => item.href && item.label).map((item) => {
              const isActive = activeMenuItem?.href === item.href
              const hasSubItems = item.items && item.items.length > 0
              const isExpanded = expandedItem === item.href
              const Icon = isActive ? item.fillIcon : item.icon

              return (
                <div key={item.href}>
                  <NavItem isActive={isActive} isCollapsed={isCollapsed}>
                    <NextLinkFromReactRouter to={item.href}>
                      <NavLink
                        isActive={isActive}
                        isCollapsed={isCollapsed}
                        onClick={(e) => {
                          if (hasSubItems && !isCollapsed) {
                            e.preventDefault()
                            handleItemClick(item.href, hasSubItems)
                          }
                        }}
                      >
                        <Icon />
                        <NavText isCollapsed={isCollapsed}>{item.label}</NavText>
                      </NavLink>
                    </NextLinkFromReactRouter>
                  </NavItem>

                  {hasSubItems && (
                    <SubNavContainer 
                      isOpen={isExpanded} 
                      isCollapsed={isCollapsed}
                    >
                      {item.items.filter(subItem => subItem.href && subItem.label).map((subItem) => (
                        <NextLinkFromReactRouter key={subItem.href} to={subItem.href}>
                          <SubNavItem>
                            {subItem.label}
                          </SubNavItem>
                        </NextLinkFromReactRouter>
                      ))}
                    </SubNavContainer>
                  )}
                </div>
              )
            })}
          </SidebarNav>

          <SidebarFooter isCollapsed={isCollapsed}>
            {!isCollapsed && (
              <PriceDisplay isCollapsed={isCollapsed}>
                {formattedGrxPrice ? `GRX: $${formattedGrxPrice}` : 'GRX: --'}
              </PriceDisplay>
            )}
          </SidebarFooter>
        </SidebarContainer>
      </DesktopSidebar>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav>
        {menuItems.filter(item => item.href && item.label).slice(0, 5).map((item) => {
          const isActive = activeMenuItem?.href === item.href
          const Icon = isActive ? item.fillIcon : item.icon

          return (
            <NextLinkFromReactRouter key={item.href} to={item.href}>
              <MobileNavItem isActive={isActive}>
                <MobileNavIcon isActive={isActive}>
                  <Icon />
                </MobileNavIcon>
                <MobileNavText isActive={isActive}>
                  {item.label}
                </MobileNavText>
              </MobileNavItem>
            </NextLinkFromReactRouter>
          )
        })}
      </MobileBottomNav>

      <MainContent isCollapsed={isCollapsed}>
        {children}
      </MainContent>
    </>
  )
}

export default Sidebar
